import React, { useState, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';
import InternalMessenger from '../components/InternalMessenger'; 
import Scheduler from '../components/Scheduler';
import { sendEmailVerification, updateProfile } from 'firebase/auth';
import { doc, updateDoc, collection, query, where, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import TeacherOverview from '../components/TeacherOverview';

const TeacherDashboard = () => {
    const [user] = useAuthState(auth);
    const [activeTab, setActiveTab] = useState('Overview');
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    // Data States
    const [studentsList, setStudentsList] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null); // For Detail View
    const [lexiconData, setLexiconData] = useState({ word: '', translation: '', definition: '', studentId: '' });

    const tabs = [
        { id: 'Overview', label: '📊 Overview' },
        { id: 'Students', label: '🎓 My Students' },
        { id: 'Schedule', label: '📅 Schedule' },
        { id: 'Lexicon', label: '📖 Lexicon Manager' },
        { id: 'Assignments', label: '📝 Assignments' },
        { id: 'Earnings', label: '💰 Earnings' },
    ];

    // --- FETCH DATA ---
    useEffect(() => {
        if (user && (activeTab === 'Students' || activeTab === 'Lexicon')) {
            fetchStudents();
        }
    }, [user, activeTab]);

    const fetchStudents = async () => {
        // In a real app, you'd fetch from `teachers/{uid}/students`. 
        // For this demo, we fetch ALL students so you can see the UI working immediately.
        try {
            const q = query(collection(db, 'users'), where('role', '==', 'student'));
            const snapshot = await getDocs(q);
            const students = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setStudentsList(students);
        } catch (err) {
            console.error("Error fetching students:", err);
        }
    };

    // --- ACTIONS ---
    const handleAddLexicon = async (e) => {
        e.preventDefault();
        if (!lexiconData.word || !lexiconData.studentId) return alert("Word and Student are required.");

        try {
            // Save to the specific user's subcollection
            await addDoc(collection(db, 'users', lexiconData.studentId, 'lexicon'), {
                word: lexiconData.word,
                translation: lexiconData.translation,
                definition: lexiconData.definition,
                addedBy: user.uid,
                createdAt: serverTimestamp(),
                status: 'new' // new, learning, mastered
            });
            alert(`Added "${lexiconData.word}" to student's lexicon!`);
            setLexiconData({ ...lexiconData, word: '', translation: '', definition: '' }); // Clear form
        } catch (err) {
            console.error(err);
            alert("Failed to add word.");
        }
    };

    const handleUpdateName = async () => {
        const firstName = prompt("First Name:");
        const lastName = prompt("Last Name:");
        if (firstName) {
            await updateProfile(user, { displayName: `${firstName} ${lastName}` });
            window.location.reload();
        }
    };

    const handleResendVerification = async () => {
        if(user) await sendEmailVerification(user);
        alert("Email sent!");
    };

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col md:flex-row relative">
            
            {/* MOBILE HEADER */}
            <div className="md:hidden bg-white p-4 shadow flex justify-between items-center z-20 relative">
                <h1 className="text-xl font-bold text-indigo-700">Aulice Teacher</h1>
                <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-2xl text-gray-700">
                    {isMobileMenuOpen ? '✖' : '☰'}
                </button>
            </div>

            {/* MOBILE OVERLAY */}
            {isMobileMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden" onClick={() => setIsMobileMenuOpen(false)}></div>}

            {/* SIDEBAR */}
            <div className={`fixed inset-y-0 left-0 transform ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:relative md:translate-x-0 transition-transform duration-300 ease-in-out w-64 bg-white shadow-lg z-40 flex flex-col h-full`}>
                <div className="p-6 border-b">
                    <h1 className="text-2xl font-bold text-indigo-700 hidden md:block">Aulice Teacher</h1>
                    <div className="mt-4">
                        <p className="font-semibold text-gray-800">Welcome, {user?.displayName?.split(' ')[0] || 'Mentor'}</p>
                        <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    {!user?.displayName && <button onClick={handleUpdateName} className="mt-2 text-xs text-indigo-600 underline">+ Add My Name</button>}
                    {!user?.emailVerified && <button onClick={handleResendVerification} className="mt-4 w-full bg-yellow-100 text-yellow-800 text-xs py-1 rounded">Resend Email</button>}
                </div>
                <nav className="flex-1 overflow-y-auto mt-2">
                    {tabs.map(tab => (
                        <button key={tab.id} onClick={() => { setActiveTab(tab.id); setIsMobileMenuOpen(false); }} className={`w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors ${activeTab === tab.id ? 'border-l-4 border-indigo-600 bg-indigo-50 font-semibold text-indigo-700' : 'text-gray-600'}`}>
                            {tab.label}
                        </button>
                    ))}
                </nav>
            </div>

            {/* MAIN CONTENT */}
            <div className="flex-1 p-4 md:p-8 h-screen overflow-y-auto">
                <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{tabs.find(t => t.id === activeTab)?.label}</h2>
                    <button onClick={() => setIsChatOpen(!isChatOpen)} className="bg-indigo-600 text-white px-4 py-2 rounded-full shadow w-full md:w-auto">⚡ The Synapse</button>
                </header>

                <div className="bg-white rounded-xl shadow p-4 md:p-6 min-h-[500px]">
                    
                    {/* --- SCHEDULE TAB --- */}
                    {activeTab === 'Schedule' && <Scheduler user={user} role="teacher" />}

                    {/* --- MY STUDENTS TAB --- */}
                    {activeTab === 'Students' && (
                        <div className="flex flex-col md:flex-row gap-6 h-full">
                            {/* Left List */}
                            <div className="w-full md:w-1/3 border-r pr-4">
                                <h3 className="font-bold text-gray-500 text-xs uppercase mb-3">Student Roster</h3>
                                {studentsList.length === 0 ? <p className="text-sm text-gray-400">Loading students...</p> : (
                                    <ul className="space-y-2">
                                        {studentsList.map(student => (
                                            <li 
                                                key={student.id} 
                                                onClick={() => setSelectedStudent(student)}
                                                className={`p-3 rounded cursor-pointer flex justify-between items-center ${selectedStudent?.id === student.id ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-gray-50 border border-transparent'}`}
                                            >
                                                <div>
                                                    <p className="font-bold text-gray-800 text-sm">{student.displayName || "Unknown"}</p>
                                                    <p className="text-xs text-gray-500">{student.email}</p>
                                                </div>
                                                <span className="text-xs bg-gray-200 px-2 py-1 rounded text-gray-600">{student.cefrLevel || "N/A"}</span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            
                            {/* Right Details */}
                            <div className="w-full md:w-2/3 pl-0 md:pl-4">
                                {selectedStudent ? (
                                    <div>
                                        <div className="flex justify-between items-start mb-6">
                                            <div>
                                                <h2 className="text-2xl font-bold text-indigo-900">{selectedStudent.displayName}</h2>
                                                <p className="text-gray-500 text-sm">Joined: {selectedStudent.createdAt?.toDate().toLocaleDateString() || 'Recently'}</p>
                                            </div>
                                            <div className="bg-green-100 text-green-800 px-3 py-1 rounded font-bold text-sm">
                                                Level: {selectedStudent.cefrLevel || 'Pending'}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-6">
                                            <div className="p-4 bg-gray-50 rounded border">
                                                <h4 className="font-bold text-xs text-gray-500 uppercase">Target Industry</h4>
                                                <p className="font-semibold">Aviation / Pilot</p>
                                            </div>
                                            <div className="p-4 bg-gray-50 rounded border">
                                                <h4 className="font-bold text-xs text-gray-500 uppercase">Last Assessment</h4>
                                                <p className="font-semibold">{selectedStudent.diagnosticScore ? `${selectedStudent.diagnosticScore.toFixed(0)}%` : 'Not taken'}</p>
                                            </div>
                                        </div>

                                        <div className="mb-6">
                                            <h4 className="font-bold text-gray-800 mb-2">Teacher Notes</h4>
                                            <textarea className="w-full border p-3 rounded h-32 text-sm" placeholder="Add private notes about this student's progress..."></textarea>
                                            <button className="mt-2 bg-indigo-600 text-white px-4 py-2 rounded text-sm hover:bg-indigo-700">Save Note</button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="h-full flex items-center justify-center text-gray-400 italic">
                                        Select a student to view their profile.
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* --- LEXICON MANAGER TAB --- */}
                    {activeTab === 'Lexicon' && (
                        <div>
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="font-bold text-xl text-gray-800">Lexicon Assignment</h3>
                                <button className="text-indigo-600 underline text-sm">View Global Glossary</button>
                            </div>

                            <div className="grid md:grid-cols-2 gap-8">
                                {/* Form */}
                                <div className="bg-gray-50 p-6 rounded-lg border">
                                    <h4 className="font-bold text-indigo-900 mb-4">Add New Word</h4>
                                    <form onSubmit={handleAddLexicon} className="space-y-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Target Student</label>
                                            <select 
                                                className="w-full border p-2 rounded"
                                                value={lexiconData.studentId}
                                                onChange={(e) => setLexiconData({...lexiconData, studentId: e.target.value})}
                                            >
                                                <option value="">-- Select Student --</option>
                                                {studentsList.map(s => <option key={s.id} value={s.id}>{s.displayName || s.email}</option>)}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Word / Term</label>
                                            <input 
                                                type="text" 
                                                className="w-full border p-2 rounded" 
                                                placeholder="e.g., Force Majeure"
                                                value={lexiconData.word}
                                                onChange={(e) => setLexiconData({...lexiconData, word: e.target.value})}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Definition (English)</label>
                                            <textarea 
                                                className="w-full border p-2 rounded" 
                                                placeholder="Explanation in English..."
                                                value={lexiconData.definition}
                                                onChange={(e) => setLexiconData({...lexiconData, definition: e.target.value})}
                                            ></textarea>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Translation (Native)</label>
                                            <input 
                                                type="text" 
                                                className="w-full border p-2 rounded" 
                                                placeholder="Optional translation..."
                                                value={lexiconData.translation}
                                                onChange={(e) => setLexiconData({...lexiconData, translation: e.target.value})}
                                            />
                                        </div>
                                        <button type="submit" className="w-full bg-green-600 text-white py-2 rounded font-bold hover:bg-green-700 shadow">
                                            + Assign Word
                                        </button>
                                    </form>
                                </div>

                                {/* Quick Preview / Tips */}
                                <div>
                                    <div className="bg-blue-50 p-6 rounded-lg border border-blue-100 mb-6">
                                        <h4 className="font-bold text-blue-900 mb-2">💡 Did you know?</h4>
                                        <p className="text-sm text-blue-800">
                                            Words assigned here appear instantly in the student's <strong>"Personal Lexicon"</strong> tab on their dashboard. They will be prompted to review them before their next class.
                                        </p>
                                    </div>
                                    <h4 className="font-bold text-gray-600 mb-3">Recently Assigned</h4>
                                    <ul className="space-y-2 text-sm text-gray-500">
                                        <li className="flex justify-between border-b pb-2">
                                            <span>🛬 Approach Vector</span>
                                            <span className="italic">Assigned to John</span>
                                        </li>
                                        <li className="flex justify-between border-b pb-2">
                                            <span>⚖️ Affidavit</span>
                                            <span className="italic">Assigned to Sarah</span>
                                        </li>
                                        <li className="flex justify-between border-b pb-2">
                                            <span>🧖‍♀️ Exfoliation</span>
                                            <span className="italic">Assigned to Mike</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* --- OVERVIEW TAB (Snooker Board) --- */}
{activeTab === 'Overview' && (
    <TeacherOverview user={user} />
)}

{/* Placeholders for remaining tabs */}
{['Assignments', 'Earnings'].includes(activeTab) && (
    <div className="text-center p-10 text-gray-500">
        <h3 className="text-xl font-bold mb-2">{tabs.find(t=>t.id===activeTab).label}</h3>
        <p>Module coming soon...</p>
    </div>
)}
                </div>
            </div>
            <InternalMessenger isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
        </div>
    );
};

export default TeacherDashboard;